import React, { useState, useMemo, useCallback, useEffect } from "react";
import axios from "axios";
import Icon from "./Icon";
import InsightsTable, { categoryLabel } from "./InsightsTable";
import { getClientMachines } from "../../controllers/machineController";
import { rowInBranch } from "./branchFilter";
import { useBranch } from "../../context/BranchContext";
import FieldHelp from "../../help/FieldHelp";
import "../../pages/InsightsTab.css";

const insightCache = new Map();

function fmtPkr(val) {
  return Number(val || 0).toLocaleString("en-US");
}

function getUsername() {
  let user = null;
  try {
    const stored = localStorage.getItem("user");
    if (stored) user = JSON.parse(stored);
  } catch (e) {
    user = null;
  }
  return (
    localStorage.getItem("username") ||
    user?.username ||
    user?.userName ||
    user?.email ||
    ""
  );
}

function toIsoDate(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function defaultRange(days = 3) {
  const to = new Date();
  const from = new Date(to);
  from.setDate(from.getDate() - days + 1);
  return { from: toIsoDate(from), to: toIsoDate(to) };
}

/* Fallback list, used only until the first response arrives -- the combined endpoint
   returns the authoritative order in `categories`, so the dropdown cannot drift out
   of step with the detectors the backend actually runs. */
const FALLBACK_CATEGORIES = [
  "Voltage",
  "Current",
  "Power",
  "Power Factor",
  "Energy",
  "Frequency",
  "THD Voltage",
  "THD Current",
];

export default function EnSightTab() {
  const initialRange = useMemo(() => defaultRange(3), []);
  const { selectedBranch, machineToBranchMap, meterIdToBranchMap } = useBranch();

  const [fromDate, setFromDate] = useState(initialRange.from);
  const [toDate, setToDate] = useState(initialRange.to);

  /* Machine / severity / category are pure view filters now: they are applied to the
     rows already in memory, so changing one repaints immediately instead of firing a
     request. Only the date range is fetched, and only when Apply (or a quick range)
     commits it. */
  const [machine, setMachine] = useState("All");
  const [severity, setSeverity] = useState("All");
  const [category, setCategory] = useState("All");
  const [range, setRange] = useState({ from: initialRange.from, to: initialRange.to });

  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
  const [partialErrors, setPartialErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [availableMachines, setAvailableMachines] = useState([]);
  const [machineLoading, setMachineLoading] = useState(false);

  const getMachineBranch = useCallback((item) => {
    if (!item) return null;

    return (
      item.branch_name ||
      item.branchName ||
      item.branch ||
      machineToBranchMap[item.machine_name] ||
      meterIdToBranchMap[String(item.meter_id ?? item.machine_id)] ||
      meterIdToBranchMap[String(item.machine_id)] ||
      null
    );
  }, [machineToBranchMap, meterIdToBranchMap]);

  const filteredMachines = useMemo(() => {
    if (!availableMachines.length) return [];
    if (selectedBranch === "All") return availableMachines;

    return availableMachines.filter((item) => {
      const branchName = getMachineBranch(item);
      return item.machine_name === selectedBranch || branchName === selectedBranch;
    });
  }, [availableMachines, selectedBranch, getMachineBranch]);

  useEffect(() => {
    if (machine === "All") return;
    if (!filteredMachines.some((item) => item.machine_name === machine)) {
      setMachine("All");
    }
  }, [machine, filteredMachines]);

  // Fetch full list of client machines on mount
  useEffect(() => {
    const username = getUsername();
    if (!username) return;

    let cancelled = false;
    setMachineLoading(true);
    getClientMachines(username)
      .then((res) => {
        if (cancelled) return;

        const seen = new Set();
        const cleaned = (res.machines || []).filter((item) => {
          if (seen.has(item.machine_id)) return false;
          seen.add(item.machine_id);
          return true;
        });

        setAvailableMachines(cleaned);
      })
      .catch((err) => {
        if (!cancelled) {
          console.warn("Failed to fetch client machines list:", err);
          setAvailableMachines([]);
        }
      })
      .finally(() => {
        if (!cancelled) setMachineLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  /* One request for every category. Deliberately asks for machine=All / severity=All
     / category=All: narrowing server-side would force a round trip on every dropdown
     change, which is exactly what this replaced. */
  useEffect(() => {
    const username = getUsername();
    if (!username) {
      setError("No authenticated user session found.");
      return undefined;
    }

    const cacheKey = `from=${range.from}&to=${range.to}&username=${username}`;

    if (insightCache.has(cacheKey)) {
      setLoading(true);
      setTimeout(() => {
        const data = insightCache.get(cacheKey);
        setRows(data.insights || []);
        setPartialErrors(data.errors || {});
        if (Array.isArray(data.categories) && data.categories.length) {
          setCategories(data.categories);
        }
        setLoading(false);
      }, 300);
      return undefined;
    }

    const controller = new AbortController();
    setLoading(true);
    setError("");

    axios
      .get("/api/ensight/ensight_data", {
        params: {
          username,
          machine: "All",
          severity: "All",
          category: "All",
          from: range.from,
          to: range.to,
        },
        signal: controller.signal,
      })
      .then((res) => {
        if (res.data && res.data.status === "success") {
          insightCache.set(cacheKey, res.data);
          setRows(res.data.insights || []);
          setPartialErrors(res.data.errors || {});
          if (Array.isArray(res.data.categories) && res.data.categories.length) {
            setCategories(res.data.categories);
          }
        } else {
          setError(res.data?.message || "Failed to fetch enSight insights.");
          setRows([]);
          setPartialErrors({});
        }
      })
      .catch((err) => {
        if (axios.isCancel?.(err) || err.code === "ERR_CANCELED") return;
        setError(err.response?.data?.message || err.message || "Failed to fetch enSight insights.");
        setRows([]);
        setPartialErrors({});
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [range.from, range.to]);

  /* /client-machines carries no branch field, so every machine->branch answer comes
     from BranchContext, which fetches on mount and retries every 5s. Until it has
     answered, nothing can be attributed to a branch -- and filtering on an empty
     map would hide EVERY row rather than the wrong ones. So the filter waits. */
  const branchMapReady =
    Object.keys(meterIdToBranchMap).length > 0 ||
    Object.keys(machineToBranchMap).length > 0;

  /* Machine names the selected branch covers. Built from filteredMachines so the
     branch is resolved exactly once, by getMachineBranch, rather than by a second
     rule here that could disagree with the dropdown. */
  const branchMachineNames = useMemo(() => {
    if (selectedBranch === "All" || !branchMapReady) return null;
    return new Set(filteredMachines.map((item) => item.machine_name));
  }, [selectedBranch, filteredMachines, branchMapReady]);

  const visibleInsights = useMemo(() => rows.filter((row) => {
    /* The branch belongs in this list. It used to narrow only the Machine dropdown,
       which meant that with Machine on "All" -- the default -- every branch's rows
       stayed on screen and the summary totals counted all of them. Selecting a
       branch appeared to do nothing. */
    if (!rowInBranch(row, branchMachineNames, selectedBranch)) return false;
    if (category !== "All" && categoryLabel(row) !== category) return false;
    if (severity !== "All" && row.severity !== severity) return false;
    if (machine !== "All" && row.machine !== machine) return false;
    return true;
  }), [rows, category, severity, machine, branchMachineNames, selectedBranch]);

  const summary = useMemo(() => {
    /* Same roll-up the endpoint does: money totals skip child meters whose parent is
       already reporting, counts do not. The exception is a single-machine view -- no
       parent is on screen to double count against, so every visible row counts, which
       is what the old per-machine requests returned. */
    const dedupe = machine === "All";
    const counted = dedupe ? visibleInsights.filter((r) => !r.is_child_deduped) : visibleInsights;
    const total = (key) => counted.reduce((acc, r) => acc + (Number(r[key]) || 0), 0);

    const totalSavingsVal = Math.trunc(total("savings"));
    const totalRemedyVal = Math.round(total("remedyCost") * 100) / 100;
    const paybackMonths = totalSavingsVal > 0 && totalRemedyVal > 0
      ? Math.round((totalRemedyVal / totalSavingsVal) * 12 * 10) / 10
      : null;

    return {
      totalLoss: Math.round(total("loss") * 100) / 100,
      // trunc, not round: the endpoint casts these with Python's int(), and the two
      // THD detectors hand back fractional cost/savings.
      totalCost: Math.trunc(total("cost")),
      totalSavings: totalSavingsVal,
      totalRemedyCost: totalRemedyVal,
      paybackMonths,
      totalInsights: visibleInsights.length,
      criticalCount: visibleInsights.filter((r) => r.severity === "Critical").length,
      warningCount: visibleInsights.filter((r) => r.severity === "Warning").length,
    };
  }, [visibleInsights, machine]);

  const applyRange = (nextFrom, nextTo) => {
    setRange((prev) =>
      prev.from === nextFrom && prev.to === nextTo ? prev : { from: nextFrom, to: nextTo }
    );
  };

  const handleApply = () => applyRange(fromDate, toDate);

  const applyQuickRange = (days) => {
    const { from: fromIso, to: toIso } = defaultRange(days);
    setFromDate(fromIso);
    setToDate(toIso);
    applyRange(fromIso, toIso);
  };

  const canApply = (fromDate !== range.from || toDate !== range.to) && Boolean(fromDate) && Boolean(toDate) && fromDate <= toDate;

  const emptyTitle = category === "All"
    ? "No insights detected in this period"
    : `No ${category.toLowerCase()} insights detected in this period`;
  const emptyDesc = "Try selecting a 30D date range, or adjust the machine / severity / category filters";

  return (
    <div className="insights-page">
      <div className="page">
        <div className="page-head">
          <div>
            <h1>Insights</h1>
            <div className="subtle">Power quality &amp; energy anomalies</div>
          </div>
          <div className="status-row">
            <span className="status-pill" style={{ color: "#dc2626" }}>
              <span className="dot" />{summary.criticalCount} <FieldHelp id="ensight.criticalCount">Critical</FieldHelp>
            </span>
            <span className="status-pill" style={{ color: "#d97706" }}>
              <span className="dot" />{summary.warningCount} <FieldHelp id="ensight.warningCount">Warning</FieldHelp>
            </span>
          </div>
        </div>

        {/* Filter Controls Bar */}
        <section className="panel filters">
          <div className="filter-grid">
            <div className="field">
              <label><FieldHelp id="ensight.branch">Branch</FieldHelp></label>
              <div style={{
                minHeight: 40,
                display: "flex",
                alignItems: "center",
                padding: "0 12px",
                borderRadius: 10,
                border: "1px solid #e2e8f0",
                background: "#f8fafc",
                color: "#0f172a",
                fontSize: 13,
                fontWeight: 600,
              }}>
                {selectedBranch}
              </div>
            </div>
            <div className="field">
              <label><FieldHelp id="ensight.fromDate">From</FieldHelp></label>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </div>
            <div className="field">
              <label><FieldHelp id="ensight.toDate">To</FieldHelp></label>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </div>
            <div className="quick-ranges">
              <button type="button" className="quick-btn" onClick={() => applyQuickRange(3)}>3D</button>
              <button type="button" className="quick-btn" onClick={() => applyQuickRange(7)}>7D</button>
              <button type="button" className="quick-btn" onClick={() => applyQuickRange(30)}>30D</button>
            </div>
            <div className="field">
              <label><FieldHelp id="ensight.machine">Machine</FieldHelp></label>
              <select value={machine} onChange={(e) => setMachine(e.target.value)} disabled={machineLoading}>
                <option value="All">
                  {machineLoading ? "Loading machines..." : "All Machines"}
                </option>
                {!machineLoading && filteredMachines.map((item) => (
                  <option key={item.machine_id} value={item.machine_name}>{item.machine_name}</option>
                ))}
              </select>
            </div>
            <div className="field">
              <label><FieldHelp id="ensight.severity">Severity</FieldHelp></label>
              <select value={severity} onChange={(e) => setSeverity(e.target.value)}>
                <option value="All">All Severities</option>
                <option value="Critical">Critical</option>
                <option value="Warning">Warning</option>
              </select>
            </div>
            <div className="field">
              <label><FieldHelp id="ensight.category">Category</FieldHelp></label>
              <select value={category} onChange={(e) => setCategory(e.target.value)}>
                <option value="All">All Categories</option>
                {categories.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              className="primary-btn"
              onClick={handleApply}
              disabled={!canApply}
            >
              Apply
            </button>
          </div>
        </section>

        {/* Summary Metric Cards */}
        <section className="summary-grid">
          <div className="stat-card" style={{ background: "#002f46" }}>
            <Icon name="chart" className="stat-icon" size={36} />
            <div>
              <p className="stat-label">
                <FieldHelp id="ensight.totalLoss">
                  {category === "All" ? "Total Loss" : `${category} Loss`}
                </FieldHelp>
              </p>
              <div className="stat-value">PKR {fmtPkr(summary.totalLoss)}</div>
              <div className="stat-sub">
                {category === "All" ? "In selected period" : `For ${category} in period`}
              </div>
            </div>
          </div>
          <div className="stat-card" style={{ background: "#ff9c07" }}>
            <Icon name="tool" className="stat-icon" size={36} />
            <div>
              <p className="stat-label">
                <FieldHelp id="ensight.totalRemedyCost">
                  {category === "All" ? "Estimated Total Cost" : `${category} Total Cost`}
                </FieldHelp>
              </p>
              <div className="stat-value">PKR {fmtPkr(summary.totalRemedyCost)}</div>
              <div className="stat-sub">
                Est. investment for fixes
              </div>
            </div>
          </div>
          <div className="stat-card" style={{ background: "#009f8d" }}>
            <Icon name="check" className="stat-icon" size={36} />
            <div>
              <p className="stat-label">
                <FieldHelp id="ensight.totalSavings">
                  {category === "All" ? "Total Savings Potential" : `${category} Savings`}
                </FieldHelp>
              </p>
              <div className="stat-value" style={{ fontSize: "20px", fontWeight: "700" }}>
                Coming Soon
              </div>
              <div className="stat-sub">If all fixes are implemented</div>
            </div>
          </div>
        </section>

        <InsightsTable
          insights={visibleInsights}
          summary={summary}
          loading={loading}
          error={error}
          emptyTitle={emptyTitle}
          emptyDesc={emptyDesc}
        />
      </div>
    </div>
  );
}
